declare const __VITE_GOOGLE_MAPS_API_KEY__: string;
declare const GOOGLE_MAPS_API_KEY: string;

// Track if the API is already loaded or loading
let isGoogleMapsLoaded = false;
let isGoogleMapsLoading = false;
let googleMapsLoadPromise: Promise<void> | null = null;
let configValidated = false; // Track if config has been validated
let loadAttempts = 0; // Track number of load attempts

// Cache for geocoding results to reduce API calls
interface GeocodingCacheEntry {
  result: { lat: number; lng: number };
  timestamp: number;
}

// Cache for geocoding results (address to coordinates)
const geocodingCache: Record<string, GeocodingCacheEntry> = {};
// Cache expiration time (24 hours in milliseconds)
const CACHE_EXPIRATION = 24 * 60 * 60 * 1000;

// Check if Google Maps is already loaded in the window
const checkIfGoogleMapsLoaded = (): boolean => {
  return typeof window !== 'undefined' && 
         window.google !== undefined && 
         window.google.maps !== undefined;
};

export const getGoogleMapsApiKey = () => {
  const apiKey = GOOGLE_MAPS_API_KEY || __VITE_GOOGLE_MAPS_API_KEY__ || import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
  
  if (!apiKey) {
    console.error('Google Maps API key is not set. Please check your environment variables.');
    throw new Error('MissingKeyMapError: Google Maps API key is not available');
  }
  
  return apiKey;
};

export const loadGoogleMapsApi = (verbose = false): Promise<void> => {
  // Always check the window object first
  if (checkIfGoogleMapsLoaded()) {
    isGoogleMapsLoaded = true;
    isGoogleMapsLoading = false;
    if (verbose) console.log('Google Maps API already loaded in window object');
    return Promise.resolve();
  }
  
  // If already loaded by our code, return resolved promise
  if (isGoogleMapsLoaded) {
    if (verbose) console.log('Google Maps API already loaded, reusing instance');
    return Promise.resolve();
  }
  
  // If currently loading, return the existing promise
  if (isGoogleMapsLoading && googleMapsLoadPromise) {
    if (verbose) console.log('Google Maps API already loading, waiting for existing promise');
    return googleMapsLoadPromise;
  }
  
  // Limit repeated load attempts
  loadAttempts++;
  if (loadAttempts > 3) {
    console.warn(`Multiple Google Maps load attempts (${loadAttempts}), possible implementation issue`);
    
    // After too many attempts, just check if it's actually there and resolve if it is
    if (checkIfGoogleMapsLoaded()) {
      isGoogleMapsLoaded = true;
      isGoogleMapsLoading = false;
      console.log('Google Maps was already loaded but not detected correctly. Fixed reference.');
      return Promise.resolve();
    }
  }
  
  // Start loading
  isGoogleMapsLoading = true;
  
  googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
    try {
      // Set timeout for the overall process
      const timeout = setTimeout(() => {
        isGoogleMapsLoading = false;
        reject(new Error('Google Maps API loading timed out after 15 seconds'));
      }, 15000);
      
      // Try to get API key 
      let apiKey;
      try {
        apiKey = getGoogleMapsApiKey();
      } catch (error) {
        clearTimeout(timeout);
        isGoogleMapsLoading = false;
        reject(error);
        return;
      }
      
      // Check again if the API is already loaded (double check)
      if (checkIfGoogleMapsLoaded()) {
        if (verbose) console.log('Google Maps API already loaded in window object (double-check)');
        isGoogleMapsLoaded = true;
        isGoogleMapsLoading = false;
        clearTimeout(timeout);
        resolve();
        return;
      }
      
      // Create callback function - use a globally unique name to prevent conflicts
      const callbackName = `googleMapsInitialized_${Date.now()}`;
      (window as any)[callbackName] = () => {
        // Only log on first load
        if (!isGoogleMapsLoaded && verbose) {
          console.log('Google Maps API loaded successfully');
        }
        isGoogleMapsLoaded = true;
        isGoogleMapsLoading = false;
        clearTimeout(timeout);
        resolve();
        delete (window as any)[callbackName];
      };
      
      // Check if there's already a Google Maps script in the document
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existingScript) {
        console.log('Found existing Google Maps script tag, will not add another');
        // If script exists but Google isn't loaded yet, wait for it
        const checkLoaded = setInterval(() => {
          if (checkIfGoogleMapsLoaded()) {
            clearInterval(checkLoaded);
            clearTimeout(timeout);
            isGoogleMapsLoaded = true;
            isGoogleMapsLoading = false;
            resolve();
          }
        }, 200);
        
        // Also set a timeout for this check
        setTimeout(() => {
          clearInterval(checkLoaded);
        }, 10000);
        
        return;
      }
      
      // Create script element with reduced libraries to minimize quota usage
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&v=beta&region=AU&language=en&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      script.id = 'google-maps-script';
      
      script.onerror = (error) => {
        console.error('Error loading Google Maps API:', error);
        isGoogleMapsLoading = false;
        clearTimeout(timeout);
        
        // Check if the error is related to quota
        const errorEvent = error as ErrorEvent;
        if (errorEvent.message && errorEvent.message.includes('quota')) {
          reject(new Error('OverQuotaMapError: Google Maps API quota exceeded'));
        } else if (errorEvent.message && errorEvent.message.includes('referer')) {
          reject(new Error('RefererNotAllowedMapError: This website is not allowed to use the Google Maps API'));
        } else {
          reject(new Error('Failed to load Google Maps API: ' + (errorEvent.message || 'Unknown error')));
        }
      };
      
      document.head.appendChild(script);
    } catch (error) {
      isGoogleMapsLoading = false;
      reject(error);
    }
  });
  
  return googleMapsLoadPromise;
};

export const constructStaticMapUrl = (center: { lat: number; lng: number }, markers: Array<{ lat: number; lng: number; label: string }>) => {
  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) return '';

  const baseUrl = 'https://maps.googleapis.com/maps/api/staticmap';
  const size = '800x200';
  const zoom = 12;
  
  // Limit the number of markers to reduce URL length and API usage
  const maxMarkers = 5;
  const limitedMarkers = markers.slice(0, maxMarkers);
  
  let url = `${baseUrl}?center=${center.lat},${center.lng}&zoom=${zoom}&size=${size}&key=${apiKey}`;
  
  limitedMarkers.forEach((marker, index) => {
    url += `&markers=color:red%7Clabel:${marker.label.charAt(0)}%7C${marker.lat},${marker.lng}`;
  });
  
  return url;
};

export const validateGoogleMapsConfig = (): boolean => {
  // If already validated, return true without logging
  if (configValidated) {
    return true;
  }
  
  try {
    const apiKey = getGoogleMapsApiKey();
    
    if (!apiKey) {
      throw new Error('MissingKeyMapError: Google Maps API key is not available');
    }
    
    if (apiKey.length < 20) {
      console.warn('Google Maps API key seems too short, it may be invalid');
    }
    
    // Only log on first validation
    console.log('Google Maps Configuration Check:', {
      hasApiKey: true,
      keyLength: apiKey.length,
      keyPrefix: apiKey.substring(0, 4) + '...',
      isLoaded: isGoogleMapsLoaded,
      isLoading: isGoogleMapsLoading
    });
    
    configValidated = true;
    return true;
  } catch (error) {
    console.error('Google Maps validation failed:', error);
    throw error;
  }
};

// Geocode an address to coordinates with caching
export const geocodeAddress = async (address: string): Promise<{ lat: number; lng: number }> => {
  // Check cache first
  const cacheKey = address.toLowerCase().trim();
  const cachedResult = geocodingCache[cacheKey];
  
  // If we have a valid cached result that hasn't expired, use it
  if (cachedResult && (Date.now() - cachedResult.timestamp) < CACHE_EXPIRATION) {
    console.log('Using cached geocoding result for:', address);
    return cachedResult.result;
  }
  
  // Ensure Google Maps API is loaded
  await loadGoogleMapsApi();
  
  return new Promise((resolve, reject) => {
    try {
      const geocoder = new google.maps.Geocoder();
      
      geocoder.geocode({ address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
          const location = results[0].geometry.location;
          const result = { 
            lat: location.lat(), 
            lng: location.lng() 
          };
          
          // Cache the result
          geocodingCache[cacheKey] = {
            result,
            timestamp: Date.now()
          };
          
          resolve(result);
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    } catch (error) {
      reject(error);
    }
  });
};

// Calculate distance between two points (haversine formula)
// This avoids using the Google Maps Distance Matrix API for simple distance calculations
export const calculateDistance = (
  point1: { lat: number; lng: number },
  point2: { lat: number; lng: number }
): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (point2.lat - point1.lat) * Math.PI / 180;
  const dLng = (point2.lng - point1.lng) * Math.PI / 180;
  
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(point1.lat * Math.PI / 180) * Math.cos(point2.lat * Math.PI / 180) * 
    Math.sin(dLng/2) * Math.sin(dLng/2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distance = R * c; // Distance in km
  
  return distance;
}; 